import { useState, useEffect, useMemo } from 'react';
import { useClinic } from '../ClinicProvider';

// --- Types ---
export interface ClinicCurrencyDetails {
  clinic__name: string;
  department__name: string;
  phone_number: string;
  clinic_id: number;
  department_id: number;
  is_default: boolean;
  area: string | null;
  city: string | null;
  country: string | null;
  landmark: string | null;
  line_1: string | null;
  line_2: string | null;
  pincode: string | null;
  state: string | null;
  currency: string;
  currency_conversion_rate: number;
  invoice_due_days: number;
  max_bill_credit_amount: number;
  clinic__name_label: string;
  department__name_label: string;
}

export interface CurrencyDetails {
  currency: string;
  currencySymbol: string;
  currencyConversionRate: number;
  invoiceDueDays: number;
  maxBillCreditAmount: number;
  clinicCurrencyDetails: ClinicCurrencyDetails | null;
  clinicName: string;
  formatCurrency: (amount: number) => string;
  formatNumber: (amount: number) => string;
  getInvoiceDueDate: (startDate?: Date) => Date;
}

// --- Utilities ---
const currencySymbolMap: Record<string, string> = {
  USD: '$', US: '$', EUR: '€', GBP: '£', INR: '₹', IND: '₹',
  JPY: '¥', CNY: '¥', AUD: 'A$', CAD: 'C$', CHF: 'CHF',
  AED: 'AED', SAR: '﷼', SGD: 'S$', MYR: 'RM', THB: '฿',
  PHP: '₱', IDR: 'Rp', VND: '₫', KRW: '₩', BRL: 'R$',
  MXN: 'MX$', ZAR: 'R', RUB: '₽', TRY: '₺', PLN: 'zł',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', HKD: 'HK$', NZD: 'NZ$',
  KWD: 'KD', BHD: 'BD', OMR: 'OMR', QAR: 'QR',
};

// Currencies where symbol appears AFTER the number (suffix) AED: 'د.إ'
const suffixCurrencies = new Set(['AED', 'SAR', 'KWD', 'BHD', 'OMR', 'QAR']);

export const getCurrencySymbol = (currency: string): string =>
  currencySymbolMap[currency.toUpperCase()] || currency;

const isSuffixCurrency = (currency: string): boolean =>
  suffixCurrencies.has(currency.toUpperCase());

/**
 * Custom hook to get currency details based on current clinic
 * Uses ClinicId from ClinicProvider and reads clinic data from localStorage
 * 
 * @returns CurrencyDetails object with currency info and formatCurrency function
 * 
 * @example
 * const { currency, currencySymbol, formatCurrency } = useCurrency();
 * console.log(formatCurrency(1500)); // "$1,500.00" or "₹1,500.00" based on clinic
 */
export const useCurrency = (): CurrencyDetails => {
  const { ClinicId } = useClinic();
  const [clinicCurrencyDetails, setClinicCurrencyDetails] =
    useState<ClinicCurrencyDetails | null>(null);

  useEffect(() => {
    const loadCurrencyDetails = () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          setClinicCurrencyDetails(null);
          return;
        }
        const user = JSON.parse(userStr);
        const clinics: ClinicCurrencyDetails[] = Array.isArray(user?.clinics) ? user.clinics : [];
        if (!ClinicId || clinics.length === 0) {
          setClinicCurrencyDetails(null);
          return;
        }
        const matchedClinic = clinics.find(
          (clinic) => String(clinic.clinic_id) === String(ClinicId)
        );
        setClinicCurrencyDetails(matchedClinic ?? clinics[0] ?? null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading currency details from localStorage:', error);
        setClinicCurrencyDetails(null);
      }
    };

    loadCurrencyDetails();

    // Listen for storage changes
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user') loadCurrencyDetails();      
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [ClinicId]);

  const currencyDetails = useMemo<CurrencyDetails>(() => {
    const currency = clinicCurrencyDetails?.currency || 'USD';
    const currencySymbol = getCurrencySymbol(currency);
    // Debug: Remove after verifying
    const invoiceDueDays = clinicCurrencyDetails?.invoice_due_days ?? 30;
    const clinicName =
      clinicCurrencyDetails?.clinic__name_label ||
      clinicCurrencyDetails?.clinic__name ||
      '';

    const formatCurrency = (amount: number): string => {
      try {
        // Format number with proper thousand separators and decimals
        const formattedNumber = new Intl.NumberFormat(navigator.language || 'en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
        // Place symbol before or after based on currency convention
        if (isSuffixCurrency(currency)) {
          return `${formattedNumber} ${currencySymbol}`;
        }
        return `${currencySymbol} ${formattedNumber}`;
      } catch {
        if (isSuffixCurrency(currency)) {
          return `${amount.toFixed(2)} ${currencySymbol}`;
        }
        return `${currencySymbol} ${amount.toFixed(2)}`;
      }
    };

    const formatNumber = (amount: number): string => {
      try {
        return new Intl.NumberFormat(navigator.language || 'en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch {
        return amount.toFixed(2);
      }
    };

    /**
     * Calculate invoice due date based on clinic's invoice_due_days setting
     * @param startDate - Optional start date (defaults to current date)
     * @returns Due date calculated by adding invoice_due_days to start date
     */
    const getInvoiceDueDate = (startDate: Date = new Date()): Date => {
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + invoiceDueDays);
      return dueDate;
    };

    return {
      currency,
      currencySymbol,
      currencyConversionRate: clinicCurrencyDetails?.currency_conversion_rate ?? 0,
      invoiceDueDays,
      maxBillCreditAmount: clinicCurrencyDetails?.max_bill_credit_amount ?? 10000,   //temp added 10000
      clinicCurrencyDetails,
      clinicName,
      formatCurrency,
      formatNumber,
      getInvoiceDueDate,
    };
  }, [clinicCurrencyDetails]);

  return currencyDetails;
};

export default useCurrency;